import React, { useEffect, useMemo, useState } from 'react';

import { Button, Modal } from '@copilot/ui';
import Icon from '../common/Icon';
import DocumentUpload from './DocumentUpload';
import ManualDocumentForm from './ManualDocumentForm';
import * as documentService from '../../services/documentService';
import { reportError } from '../../services/errorTracking';

function formatDate(isoString) {
  if (!isoString) return 'Updated recently';

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return 'Updated recently';

  return `Updated ${date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`;
}

const PROCESSING_LABELS = {
  pending: 'Processing',
  processing: 'Processing',
  completed: 'Ready',
  failed: 'Failed',
};

function documentType(document) {
  return (document.file_type || document.file_name.split('.').pop() || 'file')
    .toUpperCase();
}

// Documents the backend is still reading; their "Exact Document Answers" switch
// stays off until they are `completed`.
const UNSETTLED = new Set(['pending', 'processing']);

function DocumentSelector({ selectedDocuments, onChange, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const selectedIds = useMemo(
    () => new Set(selectedDocuments.map((document) => document.id)),
    [selectedDocuments]
  );

  useEffect(() => {
    const trimmedQuery = query.trim();
    setSuccessMessage('');

    let cancelled = false;
    // No debounce delay for the empty-query (browse) case - only an
    // actual keystroke-driven search is debounced.
    const timeout = setTimeout(
      async () => {
        setIsSearching(true);
        setError('');

        try {
          // Empty query = browse the caller's existing documents (this
          // is what a user sees the instant the picker opens, before
          // typing anything) rather than an empty "type to search"
          // prompt with nothing listed.
          const data = trimmedQuery
            ? await documentService.searchDocuments(trimmedQuery)
            : await documentService.getDocuments();
          if (!cancelled) setResults(data);
        } catch (err) {
          reportError('document_selector_search', err);
          if (!cancelled) {
            setResults([]);
            setError('Unable to load documents');
          }
        } finally {
          if (!cancelled) setIsSearching(false);
        }
      },
      trimmedQuery ? 250 : 0
    );

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [query]);

  // A freshly uploaded file is `processing` for a little while. While the
  // plain list is showing, look again until nothing is unsettled, so its
  // "Exact Document Answers" switch becomes available without reopening this.
  const hasUnsettled = !query.trim() && results.some((d) => UNSETTLED.has(d.processing_status));
  useEffect(() => {
    if (!hasUnsettled) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const data = await documentService.getDocuments();
        if (!cancelled) setResults(data);
      } catch {
        // The next tick tries again; the list on screen is still valid.
      }
    }, 4000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [hasUnsettled]);

  // A document joins the meeting with "Exact Document Answers" OFF: the user turns
  // it on, per document, themselves.
  const addDocument = (document) => {
    if (selectedIds.has(document.id)) return;
    onChange([...selectedDocuments, { ...document, use_for_ai_answers: false }]);
    setSuccessMessage('Document added to AI context');
  };

  const setUseForAiAnswers = (documentId, value) => {
    onChange(
      selectedDocuments.map((document) =>
        document.id === documentId
          ? { ...document, use_for_ai_answers: value }
          : document
      )
    );
  };

  const removeDocument = (documentId) => {
    onChange(selectedDocuments.filter((document) => document.id !== documentId));
  };

  // Shared by both DocumentUpload (a new file) and ManualDocumentForm (a
  // typed title/content) - either way the new document goes straight
  // into the selection and to the top of the results list, exactly like
  // an existing document being selected.
  const handleDocumentAdded = (document) => {
    onChange([
      ...selectedDocuments.filter((current) => current.id !== document.id),
      { ...document, use_for_ai_answers: false },
    ]);
    setResults((current) => [
      document,
      ...current.filter((result) => result.id !== document.id),
    ]);
    setSuccessMessage('Document added to AI context');
  };

  return (
    <Modal
      title="Add AI Context"
      subtitle="Search existing documents or upload a new one for this meeting."
      onClose={onClose}
      size="lg"
      actions={
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="document-selector">
        <div className="document-selector-search">
          <Icon name="search" size={17} />
          <input className="input"
            aria-label="Search documents"
            placeholder="Search documents..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        {successMessage && (
          <div className="document-selector-success">{successMessage}</div>
        )}

        {error && <div className="form-alert">{error}</div>}

        <div className="document-selector-results">
          <span className="document-selector-kicker">
            {query.trim() ? 'Results' : 'Your Documents'}
          </span>

          {isSearching && (
            <div className="document-selector-empty">
              <Icon name="file-text" size={18} />
              <p>{query.trim() ? 'Searching documents...' : 'Loading documents...'}</p>
            </div>
          )}

          {!isSearching && results.length === 0 && !error && (
            <div className="document-selector-empty">
              <Icon name="file-text" size={18} />
              <p>
                {query.trim()
                  ? 'No matching documents found.'
                  : 'No documents uploaded yet.'}
              </p>
            </div>
          )}

          {!isSearching && results.length > 0 && (
            <div className="document-selector-list">
              {results.map((document) => {
                const isSelected = selectedIds.has(document.id);
                const isReady = document.processing_status === 'completed';
                const canUseForAi = isSelected && isReady;
                const useForAi =
                  canUseForAi &&
                  selectedDocuments.find((d) => d.id === document.id)
                    ?.use_for_ai_answers === true;
                const aiTooltip = !isReady
                  ? 'Available once this document has finished processing.'
                  : !isSelected
                    ? 'Add this document to use it for AI answers.'
                    : useForAi
                      ? 'KrishvaQ can use this document to provide accurate, document-based AI answers.'
                      : 'This document will not be used for AI answers.';

                return (
                  <article
                    className={`document-selector-row ${isSelected ? 'selected' : ''}`}
                    key={document.id}
                  >
                    <span className="document-selector-file-icon">
                      <Icon name="file-text" size={18} />
                    </span>
                    <div className="document-selector-row-main">
                      <strong>{document.file_name}</strong>
                      <span>
                        {documentType(document)} - {formatDate(document.created_at)}
                        {document.processing_status
                          ? ` - ${PROCESSING_LABELS[document.processing_status] || document.processing_status}`
                          : ''}
                      </span>
                    </div>
                    <div className="document-ai-toggle" title={aiTooltip}>
                      <span className="document-ai-toggle-text">Exact Document Answers</span>
                      <button
                        type="button"
                        role="switch"
                        className="document-ai-switch"
                        aria-checked={useForAi}
                        aria-label={`Exact Document Answers: ${document.file_name}`}
                        disabled={!canUseForAi}
                        onClick={() => setUseForAiAnswers(document.id, !useForAi)}
                      >
                        <span className="document-ai-switch-thumb" aria-hidden="true" />
                      </button>
                      <span
                        className={`document-ai-toggle-state${useForAi ? ' on' : ''}`}
                        aria-hidden="true"
                      >
                        {useForAi ? 'ON' : 'OFF'}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant={isSelected ? 'secondary' : 'primary'}
                      onClick={() =>
                        isSelected ? removeDocument(document.id) : addDocument(document)
                      }
                    >
                      {isSelected ? 'Remove' : 'Add'}
                    </Button>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        <div className="document-selector-upload">
          <span className="document-selector-kicker">Upload New Document</span>
          <DocumentUpload onUploaded={handleDocumentAdded} />
        </div>

        <div className="document-selector-manual">
          <span className="document-selector-kicker">Or create manually</span>
          <ManualDocumentForm onCreated={handleDocumentAdded} />
        </div>
      </div>
    </Modal>
  );
}

export default DocumentSelector;
